/**
 * Signed URL utilities for private storage buckets
 * 
 * After photos are uploaded, we store the file path (not full URL) in the database.
 * When displaying photos, we generate short-lived signed URLs on demand.
 */

import { supabase } from "@/integrations/supabase/client";

// Signed URL expiration in seconds (24 hours)
const SIGNED_URL_EXPIRY = 60 * 60 * 24;

/**
 * Generates a signed URL for a file in a private storage bucket.
 * Falls back to returning the original path if signing fails.
 * 
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket
 * @param expiresIn - Expiration time in seconds (default: 24 hours)
 * @returns The signed URL or null if failed
 */
export async function getSignedUrl(
  bucket: string,
  path: string,
  expiresIn: number = SIGNED_URL_EXPIRY
): Promise<string | null> {
  try {
    // If it's already a full URL (legacy data), extract the path
    const cleanPath = extractPathFromUrl(path, bucket);
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(cleanPath, expiresIn);

    if (error) {
      console.error(`Error creating signed URL for ${cleanPath}:`, error);
      return null;
    }

    return data.signedUrl;
  } catch (err) {
    console.error(`Failed to get signed URL for ${path}:`, err);
    return null;
  }
}

/**
 * Generates signed URLs for multiple files in a private storage bucket.
 * 
 * @param bucket - The storage bucket name
 * @param paths - Array of file paths within the bucket
 * @param expiresIn - Expiration time in seconds (default: 24 hours)
 * @returns Array of signed URLs (null entries for failed ones)
 */
export async function getSignedUrls(
  bucket: string,
  paths: string[],
  expiresIn: number = SIGNED_URL_EXPIRY
): Promise<(string | null)[]> {
  if (!paths || paths.length === 0) return [];
  
  const cleanPaths = paths.map(p => extractPathFromUrl(p, bucket));
  
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrls(cleanPaths, expiresIn);

    if (error) {
      console.error("Error creating signed URLs:", error);
      return paths.map(() => null);
    }

    return data.map(item => item.signedUrl || null);
  } catch (err) {
    console.error("Failed to get signed URLs:", err);
    return paths.map(() => null);
  }
}

/**
 * Extracts the file path from a full public URL (for legacy data migration)
 * 
 * @param urlOrPath - Either a full URL or just the path
 * @param bucket - The bucket name to extract from URL
 * @returns Just the path portion
 */
export function extractPathFromUrl(urlOrPath: string, bucket: string): string {
  if (!urlOrPath) return urlOrPath;
  
  // If it's not a URL, return as-is
  if (!urlOrPath.startsWith("http")) {
    return urlOrPath;
  }

  // Try to extract path from Supabase storage URL
  // Format: https://{project}.supabase.co/storage/v1/object/public/{bucket}/{path}
  const publicPattern = `/storage/v1/object/public/${bucket}/`;
  const signedPattern = `/storage/v1/object/sign/${bucket}/`;
  
  let pathStart = urlOrPath.indexOf(publicPattern);
  if (pathStart !== -1) {
    return urlOrPath.substring(pathStart + publicPattern.length);
  }
  
  pathStart = urlOrPath.indexOf(signedPattern);
  if (pathStart !== -1) {
    const path = urlOrPath.substring(pathStart + signedPattern.length);
    // Remove query params from signed URLs
    return path.split("?")[0];
  }

  // Last resort: assume everything after the bucket name is the path
  const bucketIndex = urlOrPath.indexOf(`/${bucket}/`);
  if (bucketIndex !== -1) {
    return urlOrPath.substring(bucketIndex + bucket.length + 2);
  }

  return urlOrPath;
}

/**
 * Uploads a file and returns just the file path (not URL).
 * Use getSignedUrl() to get a viewable URL later.
 * 
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket
 * @param file - The file to upload
 * @returns The file path on success, null on failure
 */
export async function uploadAndGetPath(
  bucket: string,
  path: string,
  file: Blob | File
): Promise<string | null> {
  try {
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file);

    if (error) {
      console.error(`Error uploading to ${bucket}/${path}:`, error);
      return null;
    }

    // Return just the path, not a URL
    return path;
  } catch (err) {
    console.error(`Failed to upload ${path}:`, err);
    return null;
  }
}

/**
 * Hook-friendly version: gets a signed URL and returns it synchronously
 * by first checking if it's already a valid signed URL.
 * 
 * This is useful for display components that need to show photos immediately.
 * 
 * @param urlOrPath - A URL or path
 * @returns true if it looks like a currently valid URL
 */
export function isValidImageUrl(urlOrPath: string | null | undefined): boolean {
  if (!urlOrPath) return false;
  // Check if it's a data URL, blob URL, or http(s) URL
  return (
    urlOrPath.startsWith("data:") ||
    urlOrPath.startsWith("blob:") ||
    urlOrPath.startsWith("http")
  );
}
